/**
 * R12 — Pre-approve a visitor. Wired: POST /api/visitors/preapprove →
 * on success routes to /gatepass with the returned pass id + code.
 * Port of design/source/screens-visitor.jsx PreapproveScreen.
 */
import { router } from 'expo-router';
import React from 'react';
import { Platform, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { space } from '@bero/tokens';
import { CompanyChips } from '@/components/companies/CompanyPicker';
import { str } from '@/components/gate/api';
import { InfoNote, Segmented, Toggle } from '@/components/gate/bits';
import { Icon } from '@/components/gate/icons';
import { useT } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Button, Chip, Field, IconButton, Input, Screen, TopBar } from '@/ui';

const PURPOSES = ['Guest', 'Delivery', 'Cab', 'Help'];
/** UI label → backend VISITOR_TYPES enum (Guest is not a valid API value). */
const PURPOSE_TYPE: Record<string, string> = { Guest: 'VISITOR', Delivery: 'DELIVERY', Cab: 'CAB', Help: 'STAFF' };
const WHEN = ['Today', 'Tomorrow', 'Pick a date'];

/** Help quick picks; Delivery/Cab use the full CompanyPicker dataset. */
const HELP_TYPES = ['Maid', 'Cook', 'Driver', 'Electrician', 'Plumber', 'Carpenter', 'Other'];

// Display-label maps (logic values above stay English; only what's shown is translated).
const PURPOSE_LABEL: Record<string, DictKey> = {
  Guest: 'pa_purpose_guest', Delivery: 'pa_purpose_delivery', Cab: 'pa_purpose_cab', Help: 'pa_purpose_help',
};
const WHEN_LABEL: Record<string, DictKey> = {
  Today: 'pa_when_today', Tomorrow: 'pa_when_tomorrow', 'Pick a date': 'pa_when_pickdate',
};
const FREQ_LABEL: Record<string, DictKey> = { Once: 'pa_freq_once', Frequent: 'pa_freq_frequent' };
const HELP_LABEL: Record<string, DictKey> = {
  Maid: 'pa_help_maid', Cook: 'pa_help_cook', Driver: 'pa_help_driver', Electrician: 'pa_help_electrician',
  Plumber: 'pa_help_plumber', Carpenter: 'pa_help_carpenter', Other: 'pa_help_other',
};

/** Parse "6:00 PM" / "10:30 am" into 24h {h, m}; null if unparseable. */
function parseClockTime(text: string): { h: number; m: number } | null {
  const match = text.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return null;
  const h12 = parseInt(match[1], 10);
  const m = parseInt(match[2], 10);
  if (h12 < 1 || h12 > 12 || m < 0 || m > 59) return null;
  const isPM = match[3].toUpperCase() === 'PM';
  const h = isPM ? (h12 % 12) + 12 : h12 % 12;
  return { h, m };
}

/** Combine a YYYY-MM-DD date + parsed local time into a real ISO instant. */
function buildLocalIso(dateStr: string, time: { h: number; m: number } | null): string | null {
  const dm = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!dm || !time) return null;
  const d = new Date(Number(dm[1]), Number(dm[2]) - 1, Number(dm[3]), time.h, time.m, 0, 0);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/** "h:mm AM/PM" for a Date. */
function clock(d: Date): string {
  let h = d.getHours(); const am = h < 12 ? 'AM' : 'PM'; h = h % 12 || 12;
  return `${h}:${String(d.getMinutes()).padStart(2, '0')} ${am}`;
}

export default function PreapproveScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const insets = useSafeAreaInsets();

  const [name, setName] = React.useState('');
  const [purpose, setPurpose] = React.useState('Guest');
  const [phone, setPhone] = React.useState('');
  const [vehicleNo, setVehicleNo] = React.useState('');
  const [pick, setPick] = React.useState('');          // company / help-type quick pick
  const [pickOther, setPickOther] = React.useState(''); // free text when pick === 'Other'
  const [multiEntry, setMultiEntry] = React.useState(false); // Guest: allow re-entry
  const [leaveAtGate, setLeaveAtGate] = React.useState(false); // Delivery: no-answer fallback
  const [quietArrival, setQuietArrival] = React.useState(false); // Wave D: surprise — no arrival ping
  // Multi-guest invite: extra named guests beyond the primary (Guest purpose).
  const [extraGuests, setExtraGuests] = React.useState<{ name: string; phone: string }[]>([]);

  // Contacts picker (build #3). LAZY import on purpose: the module isn't in
  // the 0.2.0 binary, so this must never crash an OTA'd 0.2.0 client — the
  // button simply doesn't appear when the module is missing (or on web).
  const [contactsReady, setContactsReady] = React.useState(false);
  React.useEffect(() => {
    if (Platform.OS === 'web') return;
    import('expo-contacts')
      .then((C) => setContactsReady(typeof C.presentContactPickerAsync === 'function'))
      .catch(() => {});
  }, []);
  const pickFromContacts = async () => {
    try {
      const C = await import('expo-contacts');
      const contact = await C.presentContactPickerAsync();
      if (!contact) return;
      if (contact.name) setName(contact.name);
      const raw = contact.phoneNumbers?.[0]?.number ?? '';
      const digits = raw.replace(/\D/g, '').replace(/^91(?=\d{10}$)/, '').slice(-10);
      if (digits.length === 10) setPhone(digits);
    } catch {
      // system picker cancelled or module unavailable — nothing to do.
    }
  };
  const [when, setWhen] = React.useState('Today');
  const [pickedDate, setPickedDate] = React.useState('');
  // Default the window to NOW + a duration chip (MyGate's "valid for next X").
  const [timeFrom, setTimeFrom] = React.useState(() => clock(new Date()));
  const [validHours, setValidHours] = React.useState(4);
  // Frequent mode (Guest/Delivery/Cab): one multi-entry pass over a date range
  // with a daily time window — the office cab / regular milk-run pattern.
  const [freq, setFreq] = React.useState('Once');
  const [validDays, setValidDays] = React.useState(30);
  const isFrequent = freq === 'Frequent' && purpose !== 'Help';
  const [sendCode, setSendCode] = React.useState(true);
  const [note, setNote] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const dateValue = () => {
    const d = new Date();
    if (when === 'Tomorrow') d.setDate(d.getDate() + 1);
    if (when === 'Pick a date') return pickedDate;
    return d.toISOString().slice(0, 10);
  };

  /** Resolved company/help-type sent to the API. Delivery/Cab pick real company
   * names via CompanyChips (free text comes through the sheet's "Use …" row);
   * Help keeps type chips with an Other input. */
  const company = purpose === 'Help' && pick === 'Other' ? pickOther.trim() : pick;

  const switchPurpose = (p: string) => {
    setPurpose(p);
    setPick('');
    setPickOther('');
  };

  /** Delivery needs no name — "Amazon delivery" is a perfectly good visitor. */
  /** Auto names make Delivery/Cab/Help one-tap quick flows — no typing needed. */
  const fallbackName = purpose === 'Delivery' && company ? `${company} delivery`
    : purpose === 'Cab' && company ? `${company} cab`
    : purpose === 'Help' && company ? company
    : '';

  const submit = async () => {
    setSubmitting(true);
    setError(null);

    const dateStr = dateValue();
    const expectedAt = buildLocalIso(dateStr, parseClockTime(timeFrom));
    if (!expectedAt) {
      setError(t('pa_err_time'));
      setSubmitting(false);
      return;
    }
    // Once: until = start + N hours (crosses midnight correctly).
    // Frequent: until = start + N days; the pass is multi-entry with a daily window.
    const expectedUntil = isFrequent
      ? new Date(new Date(expectedAt).getTime() + validDays * 86400000).toISOString()
      : new Date(new Date(expectedAt).getTime() + validHours * 3600 * 1000).toISOString();
    const dailyWindow = isFrequent
      ? `${timeFrom} + ${validHours}h daily`
      : null;

    const mergedNotes = [note.trim(), leaveAtGate && purpose === 'Delivery' ? 'Leave at gate if no answer.' : '']
      .filter(Boolean).join(' ');
    try {
      const body = await api.post<unknown>('/api/visitors/preapprove', {
        name: name.trim() || fallbackName || 'Guest',
        type: PURPOSE_TYPE[purpose] ?? 'VISITOR',
        phone: phone.trim() ? `+91${phone.trim()}` : null,
        vehicleNo: purpose === 'Cab' ? vehicleNo.trim() || null : null,
        company: company || null,
        multiUse: isFrequent ? true : (purpose === 'Guest' ? multiEntry : false),
        expectedAt,
        expectedUntil,
        dailyWindow,
        quietArrival,
        notes: mergedNotes || null,
        guests: purpose === 'Guest' && extraGuests.some((x) => x.name.trim())
          ? [
            { name: name.trim() || fallbackName || 'Guest', phone: phone.trim() ? `+91${phone.trim()}` : null },
            ...extraGuests.filter((x) => x.name.trim())
              .map((x) => ({ name: x.name.trim(), phone: x.phone.trim() ? `+91${x.phone.trim()}` : null })),
          ]
          : undefined,
      });
      const passCount = (body && typeof body === 'object' && 'count' in body) ? Number((body as { count?: number }).count) : 1;
      if (passCount > 1) {
        // Each guest got their own named pass — the passes list shows them all.
        router.replace('/visitors');
        return;
      }
      const gatePass = (body && typeof body === 'object' && 'gatePass' in body) ? (body as { gatePass: unknown }).gatePass : undefined;
      const id = str(gatePass, 'id') ?? str(body, 'id');
      const code = str(gatePass, 'pin', 'code') ?? str(body, 'pin', 'code');
      const qs = new URLSearchParams();
      const shareCode = str(gatePass, 'shareCode');
      if (id) qs.set('id', id);
      qs.set('name', name.trim() || fallbackName || 'Guest');
      if (code) qs.set('code', code);
      if (shareCode) qs.set('sc', shareCode);
      qs.set('valid', isFrequent
        ? `Next ${validDays} days · ${timeFrom} + ${validHours}h daily · multiple entries`
        : `${when === 'Pick a date' ? pickedDate || 'Selected date' : when} · from ${timeFrom} for ${validHours}h · ${purpose === 'Guest' && multiEntry ? 'multiple entries' : 'single entry'}`);
      router.push(`/gatepass?${qs.toString()}`);
    } catch (e) {
      const status = (e as { status?: number }).status;
      let message = t('pa_err_generic');
      if (status === 404 || status === 501) {
        message = t('pa_err_unavailable');
      } else if (status === 400) {
        message = t('pa_err_details');
      } else if (status === 409) {
        message = t('pa_err_conflict');
      }
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Screen scroll={false}>
      <TopBar
        title={t('pa_title')}
        onBack={() => router.back()}
        right={
          <IconButton label={t('close')} onPress={() => router.back()}>
            <Icon name="x" size={20} color={colors.textPrimary} />
          </IconButton>
        }
      />
      <Screen padded style={{ paddingTop: space.s4, gap: space.s4, maxWidth: 640 }}>
        <Field label={t('pa_purpose')}>
          <Segmented options={PURPOSES} labels={PURPOSES.map((p) => t(PURPOSE_LABEL[p]))} value={purpose} onChange={switchPurpose} />
        </Field>

        {purpose !== 'Help' ? (
          <Field label={t('pa_how_often')}>
            <Segmented options={['Once', 'Frequent']} labels={['Once', 'Frequent'].map((f) => t(FREQ_LABEL[f]))} value={freq} onChange={setFreq} />
            {isFrequent ? (
              <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s2 }}>
                {t('pa_frequent_note')}
              </AppText>
            ) : null}
          </Field>
        ) : null}

        {purpose === 'Delivery' || purpose === 'Cab' ? (
          <Field label={purpose === 'Delivery' ? t('pa_delivery_company') : t('cab_service')}>
            <CompanyChips
              kind={purpose === 'Delivery' ? 'delivery' : 'cab'}
              value={pick}
              onChange={setPick}
            />
          </Field>
        ) : null}

        {purpose === 'Help' ? (
          <Field label={t('pa_type_of_help')}>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: space.s2 }}>
              {HELP_TYPES.map((c) => (
                <Chip key={c} label={t(HELP_LABEL[c])} active={pick === c} onPress={() => setPick(pick === c ? '' : c)} />
              ))}
            </View>
            {pick === 'Other' ? (
              <Input
                placeholder={t('pa_help_other_ph')}
                value={pickOther}
                onChangeText={setPickOther}
                style={{ marginTop: space.s2 }}
              />
            ) : null}
          </Field>
        ) : null}

        <Field
          label={purpose === 'Delivery' ? t('pa_name_delivery') : purpose === 'Help' ? t('pa_name_helper') : t('pa_name_visitor')}
        >
          <Input
            placeholder={purpose === 'Delivery' ? t('pa_name_ph_delivery') : purpose === 'Help' ? t('pa_name_ph_helper') : t('pa_name_ph_visitor')}
            value={name}
            onChangeText={setName}
          />
          {contactsReady && purpose === 'Guest' ? (
            <AppText
              variant="bodySm" weight="600" tone="brand" style={{ marginTop: space.s2 }}
              onPress={() => { void pickFromContacts(); }}
            >
              {t('pa_pick_contacts')}
            </AppText>
          ) : null}
        </Field>

        <Field label={t('pa_phone')} help={t('pa_phone_help')}>
          <View style={{ flexDirection: 'row', gap: space.s2 }}>
            <View style={{
              width: 56, minHeight: 44, borderRadius: 8, borderWidth: 1, borderColor: colors.border,
              alignItems: 'center', justifyContent: 'center', backgroundColor: colors.canvas0,
            }}>
              <AppText variant="bodyMd" tone="secondary">+91</AppText>
            </View>
            <Input
              placeholder="98XXXXXX21"
              value={phone}
              onChangeText={setPhone}
              keyboardType="phone-pad"
              style={{ flex: 1 }}
            />
          </View>
        </Field>

        {purpose === 'Guest' ? (
          <View>
            {extraGuests.map((gx, i) => (
              <View key={i} style={{ flexDirection: 'row', gap: space.s2, marginBottom: space.s2, alignItems: 'center' }}>
                <Input
                  placeholder={t('pa_guest_n_name', { n: i + 2 })}
                  value={gx.name}
                  onChangeText={(v) => setExtraGuests((s) => s.map((x, j) => (j === i ? { ...x, name: v } : x)))}
                  style={{ flex: 1.2 }}
                />
                <Input
                  placeholder={t('phone_opt')}
                  value={gx.phone}
                  keyboardType="phone-pad"
                  onChangeText={(v) => setExtraGuests((s) => s.map((x, j) => (j === i ? { ...x, phone: v } : x)))}
                  style={{ flex: 1 }}
                />
                <IconButton label={t('pa_remove_guest')} onPress={() => setExtraGuests((s) => s.filter((_, j) => j !== i))}>
                  <Icon name="x" size={16} color={colors.textTertiary} />
                </IconButton>
              </View>
            ))}
            {extraGuests.length < 9 ? (
              <AppText
                variant="bodySm" weight="600" tone="brand"
                onPress={() => setExtraGuests((s) => [...s, { name: '', phone: '' }])}
              >
                {t('pa_add_guest')}
              </AppText>
            ) : null}
          </View>
        ) : null}

        {purpose === 'Cab' ? (
          <Field label={t('vehicle_number')}>
            <Input
              placeholder="MH 01 AB 1234"
              value={vehicleNo}
              onChangeText={(v) => setVehicleNo(v.toUpperCase())}
              autoCapitalize="characters"
            />
          </Field>
        ) : null}

        {purpose === 'Guest' && !isFrequent ? (
          <View style={{
            backgroundColor: colors.canvas1, borderRadius: 12, padding: space.s4,
            flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <View style={{ flex: 1, paddingRight: space.s3 }}>
              <AppText variant="bodyMd" weight="500">{t('pa_multi_title')}</AppText>
              <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
                {t('pa_multi_sub')}
              </AppText>
            </View>
            <Toggle on={multiEntry} onChange={setMultiEntry} label={t('pa_multi_title')} />
          </View>
        ) : null}

        {purpose === 'Delivery' ? (
          <View style={{
            backgroundColor: colors.canvas1, borderRadius: 12, padding: space.s4,
            flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <View style={{ flex: 1, paddingRight: space.s3 }}>
              <AppText variant="bodyMd" weight="500">{t('leave_at_gate_lbl')}</AppText>
              <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
                {t('pa_leave_sub')}
              </AppText>
            </View>
            <Toggle on={leaveAtGate} onChange={setLeaveAtGate} label={t('leave_at_gate_lbl')} />
          </View>
        ) : null}

        <View style={{
          backgroundColor: colors.canvas1, borderRadius: 12, padding: space.s4,
          flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <View style={{ flex: 1, paddingRight: space.s3 }}>
            <AppText variant="bodyMd" weight="500">{t('pa_quiet_title')}</AppText>
            <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
              {t('pa_quiet_sub')}
            </AppText>
          </View>
          <Toggle on={quietArrival} onChange={setQuietArrival} label={t('pa_quiet_title')} />
        </View>

        {!isFrequent ? (
          <>
            <Field label={t('pa_expected_on')}>
              <Segmented options={WHEN} labels={WHEN.map((w) => t(WHEN_LABEL[w]))} value={when} onChange={setWhen} />
            </Field>
            {when === 'Pick a date' ? (
              <Field label={t('pa_date')}>
                <Input placeholder="e.g. 2026-07-12" value={pickedDate} onChangeText={setPickedDate} />
              </Field>
            ) : null}
          </>
        ) : (
          <Field label={t('pa_for_next')}>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: space.s2 }}>
              {[7, 15, 30, 90].map((d) => (
                <Chip key={d} label={t('pa_n_days', { n: d })} active={validDays === d} onPress={() => setValidDays(d)} />
              ))}
            </View>
          </Field>
        )}

        <Field label={isFrequent ? t('pa_arrives_around') : t('pa_starts_at')}>
          <Input value={timeFrom} onChangeText={setTimeFrom} placeholder="6:00 PM" />
        </Field>

        <Field label={isFrequent ? t('pa_daily_window') : t('pa_valid_for_next')}>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: space.s2 }}>
            {[1, 2, 4, 8, 12, 24].map((h) => (
              <Chip
                key={h}
                label={h === 24 ? t('pa_24_hours') : h === 1 ? t('pa_one_hour') : t('pa_n_hours', { n: h })}
                active={validHours === h}
                onPress={() => setValidHours(h)}
              />
            ))}
          </View>
          {isFrequent ? (
            <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s2 }}>
              {t('pa_daily_note')}
            </AppText>
          ) : null}
        </Field>

        <View style={{
          backgroundColor: colors.canvas1, borderRadius: 12, padding: space.s4,
          flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <View style={{ flex: 1, paddingRight: space.s3 }}>
            <AppText variant="bodyMd" weight="500">{t('pa_send_code_title')}</AppText>
            <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
              {t('pa_send_code_sub')}
            </AppText>
          </View>
          <Toggle on={sendCode} onChange={setSendCode} label={t('pa_send_code_title')} />
        </View>

        <Field label={t('pa_notes')}>
          <Input
            placeholder={t('pa_notes_ph')}
            value={note}
            onChangeText={setNote}
            multiline
            numberOfLines={3}
            style={{ minHeight: 80, textAlignVertical: 'top' }}
          />
        </Field>

        {error ? <InfoNote icon="info" tint={colors.danger500}>{error}</InfoNote> : null}
      </Screen>

      <View style={{
        paddingHorizontal: space.s4, paddingTop: space.s3, paddingBottom: insets.bottom + space.s3,
        borderTopWidth: 1, borderTopColor: colors.borderSoft, backgroundColor: colors.canvas0,
      }}>
        <Button
          title={(name.trim() || fallbackName) ? t('pa_submit_named', { name: (name.trim() || fallbackName).split(' ')[0] }) : t('pa_submit')}
          loading={submitting}
          onPress={submit}
        />
      </View>
    </Screen>
  );
}
